import * as React from "react";

// Device breakpoints
const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1280,
} as const;

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

export interface DeviceInfo {
  type: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  width: number;
  height: number;
  orientation: 'portrait' | 'landscape';
  hasTouch: boolean;
}

function getDeviceType(width: number): DeviceType {
  if (width < BREAKPOINTS.mobile) {return 'mobile';}
  if (width < BREAKPOINTS.tablet) {return 'tablet';}
  return 'desktop';
}

function getOrientation(width: number, height: number): 'portrait' | 'landscape' {
  return width > height ? 'landscape' : 'portrait';
}

function buildDeviceInfo(width: number, height: number, hasTouch: boolean): DeviceInfo {
  const type = getDeviceType(width);
  return {
    type,
    isMobile: type === 'mobile',
    isTablet: type === 'tablet',
    isDesktop: type === 'desktop',
    width,
    height,
    orientation: getOrientation(width, height),
    hasTouch,
  };
}

export function useDeviceDetection(): DeviceInfo {
  const [deviceInfo, setDeviceInfo] = React.useState<DeviceInfo>(() => {
    if (typeof window === 'undefined') {
      return buildDeviceInfo(1920, 1080, false);
    }
    return buildDeviceInfo(
      window.innerWidth,
      window.innerHeight,
      'ontouchstart' in window || navigator.maxTouchPoints > 0
    );
  });

  // Use a ref to track previous meaningful values to avoid unnecessary re-renders
  // from scrollbar-induced width changes (~17px oscillation)
  const prevRef = React.useRef({
    type: deviceInfo.type,
    orientation: deviceInfo.orientation,
    hasTouch: deviceInfo.hasTouch,
  });
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    const updateDeviceInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const type = getDeviceType(width);
      const orientation = getOrientation(width, height);

      // Only update state if meaningful properties changed
      // This prevents scrollbar oscillation: scrollbar appearing/disappearing
      // changes width by ~17px but doesn't change device type or orientation
      if (
        type !== prevRef.current.type ||
        orientation !== prevRef.current.orientation ||
        hasTouch !== prevRef.current.hasTouch
      ) {
        prevRef.current = { type, orientation, hasTouch };
        setDeviceInfo(buildDeviceInfo(width, height, hasTouch));
      }
    };

    const debouncedUpdate = () => {
      if (debounceRef.current) {clearTimeout(debounceRef.current);}
      debounceRef.current = setTimeout(updateDeviceInfo, 150);
    };

    const handleOrientationChange = () => {
      // Longer delay for orientation change to let viewport settle
      if (debounceRef.current) {clearTimeout(debounceRef.current);}
      debounceRef.current = setTimeout(updateDeviceInfo, 300);
    };

    window.addEventListener('resize', debouncedUpdate);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      if (debounceRef.current) {clearTimeout(debounceRef.current);}
      window.removeEventListener('resize', debouncedUpdate);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  return deviceInfo;
}

// Enhanced mobile hook for backward compatibility
export function useIsMobile(): boolean {
  const { isMobile } = useDeviceDetection();
  return isMobile;
}

// Responsive utility hooks
export function useResponsiveValue<T>(values: {
  mobile: T;
  tablet?: T;
  desktop: T;
}): T {
  const { type } = useDeviceDetection();
  
  switch (type) {
    case 'mobile':
      return values.mobile;
    case 'tablet':
      return values.tablet ?? values.desktop;
    case 'desktop':
      return values.desktop;
    default:
      return values.desktop;
  }
}

// Hook for conditional rendering based on device
export function useShowOn(deviceTypes: DeviceType[]): boolean {
  const { type } = useDeviceDetection();
  return deviceTypes.includes(type);
}